
import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { createTaskApi, getEmployeesApi } from "../../app/api";
import { createPortal } from "react-dom";

const PRIORITY_COLORS = {
  LOW: "bg-green-100 text-green-700",
  MEDIUM: "bg-yellow-100 text-yellow-700",
  HIGH: "bg-red-100 text-red-700",
};

export default function CreateTaskModal({ open, onClose, onCreated }) {

  const [search, setSearch] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [assignedTo, setAssignedTo] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dueDate, setDueDate] = useState("");

  // useEffect(() => {
  //   if (!open) return;
  //   getEmployeesApi()
  //     .then((res) => setUsers(res.data || []))
  //     .catch(() => setUsers([]));
  // }, [open]);


  useEffect(() => {
    if (!open) return;

    getEmployeesApi()
      .then((res) => {
        const allUsers = res.data || [];

        // ✅ FILTER ONLY role_id = 6
        const employeesOnly = allUsers.filter(
          (u) => Number(u.role_id) === 6
        );

        setUsers(employeesOnly);
      })
      .catch(() => setUsers([]));
  }, [open]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Task title is required");
      return;
    }

    if (!dueDate) {
      setError("Due date is required");
      return;
    }
    const payload = {
      title,
      description,
      priority,
      due_date: dueDate,   // ✅ FIXED
      assigned_to:
        assignedTo.length > 0 ? assignedTo : [user.id],
    };

    setLoading(true);
    setError(null);

    try {
      const res = await createTaskApi(payload);
      if (!res.success) throw new Error();

      onCreated();
      onClose();

      setTitle("");
      setDescription("");
      setPriority("MEDIUM");
      setAssignedTo([]);
      setDueDate("");   // 👈 ADD HERE
    } catch {
      setError("Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  // return (
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex">

          {/* Overlay */}
          <motion.div
            className="flex-1 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Right Sheet */}
          <motion.div
            className="w-[560px] bg-[#f7f9fc] shadow-2xl flex flex-col"
            initial={{ x: 600, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 600, opacity: 0 }}
            transition={{ type: "spring", stiffness: 90, damping: 20 }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-6 py-5">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-semibold">
                    Create new task
                  </h2>
                  <p className="text-sm opacity-90 mt-1">
                    Plan, assign and track work
                  </p>
                </div>
                <button onClick={onClose}>
                  <XMarkIcon className="w-6 h-6 opacity-80 hover:opacity-100" />
                </button>
              </div>
            </div>

            {/* Content */}
            <motion.div
              className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: {
                  transition: {
                    staggerChildren: 0.08,
                  },
                },
              }}
            >
              {/* Title */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="bg-white rounded-2xl p-4 shadow-sm"
              >
                <label className="text-sm font-medium text-gray-700">
                  Task title *
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-2 w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Design onboarding flow"
                />
              </motion.div>

              {/* Description */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="bg-white rounded-2xl p-4 shadow-sm"
              >
                <label className="text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="mt-2 w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional details about the task"
                />
              </motion.div>












              {/* =========deadline ---------============== */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="bg-white rounded-2xl p-4 shadow-sm"
              >
                <label className="text-sm font-medium text-gray-700">
                  Deadline *
                </label>

                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-2 w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <p className="text-xs text-gray-400 mt-1">
                  This is required to create a task
                </p>
              </motion.div>

              {/* Priority */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="bg-white rounded-2xl p-4 shadow-sm"
              >
                <label className="text-sm font-medium text-gray-700">
                  Priority
                </label>
                <div className="flex gap-3 mt-3">
                  {["LOW", "MEDIUM", "HIGH"].map((p) => (
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      key={p}
                      onClick={() => setPriority(p)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border ${priority === p
                          ? PRIORITY_COLORS[p]
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                    >
                      {p}
                    </motion.button>
                  ))}
                </div>
              </motion.div>

              {/* Assign */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="bg-white rounded-2xl p-4 shadow-sm"
              >
                {/* <label className="text-sm font-medium text-gray-700">
                  Assign to
                </label> */}

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Assign to
                  </label>

                  <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-100 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>


                <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                  {/* {users.map((u) => ( */}
                  {users
                    .filter((u) =>
                      `${u.first_name} ${u.last_name}`
                        .toLowerCase()
                        .includes(search.toLowerCase())
                    )
                    .map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-3 bg-gray-50 hover:bg-white border border-gray-200 rounded-xl px-3 py-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={assignedTo.includes(u.id)}
                          onChange={() =>
                            setAssignedTo((prev) =>
                              prev.includes(u.id)
                                ? prev.filter((x) => x !== u.id)
                                : [...prev, u.id]
                            )
                          }
                        />
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                          {u.first_name[0]}
                        </div>
                        {u.first_name} {u.last_name}
                      </label>
                    ))}
                </div>

                <div className="text-xs text-gray-500 mt-2">
                  If no one is selected, task will be assigned to you
                </div>
              </motion.div>

              {error && (
                <div className="text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-sm">
                  {error}
                </div>
              )}
            </motion.div>

            {/* Footer */}
            <div className="px-6 py-4 bg-white border-t flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300"
              >
                Cancel
              </button>

              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-500 text-white shadow-md"
              >
                {loading ? "Creating..." : "Create Task"}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
